/**
 * THE ONE PLACE AN OVERLAY IS DRAWN (FEAT-028 wave 2).
 *
 * A moment's decoration — the grey text bar you drag up and down, and anything
 * drawn on it — used to be burned into the file before sending. Now it travels
 * as DATA and is drawn wherever the moment is shown: the editor, the
 * recipient's viewer, and the copy exported out of the app. Three surfaces,
 * three different pixel sizes, one function — which is the only way they can
 * agree. Two copies of this maths is how BUG-112 happened.
 *
 * MEDIA SPACE, and nothing else. Every number below is a FRACTION of the media
 * (see `TextBar` in ../types): a bar's `y` is a percent of the picture's
 * height, a stroke's points are 0-1, and — this is BUG-112's fix — a stroke's
 * width is a fraction of the picture's WIDTH, never a count of screen pixels.
 * Feed this function a 390 px preview or a 1080 px file and the result is the
 * same picture at two sizes.
 */

/** A caption bar: the words, and how far down the picture they were dragged. */
export interface OverlayBar {
  text: string;
  /** % of the media's HEIGHT from its top (0-100) — MEDIA SPACE. */
  y: number;
}

/** One drawn stroke. Points are 0-1 on each axis; `w` is a fraction of width. */
export interface OverlayStroke {
  /** [x, y] pairs, 0-1 — packed as tuples because these go on the wire. */
  pts: [number, number][];
  /** `#RRGGBB`. */
  color: string;
  /** Line width as a fraction of the media's WIDTH (BUG-112). */
  w: number;
}

/**
 * Everything decorating one moment, versioned.
 *
 * ONE blob, not a column per feature: a future filter or sticker is a new key
 * in here, and `v` is how a client that predates it knows to be careful.
 */
export interface MomentOverlays {
  v: 1;
  bars: OverlayBar[];
  strokes: OverlayStroke[];
}

/**
 * The look of a text bar, in fractions of the media's width.
 *
 * These are the numbers the old bake used (`mediaBaking.ts`), minus its
 * `Math.max(16, Math.min(48, …))` pixel clamp — that clamp made a 1080-wide
 * file's font 4.44% while a 390-wide preview's was 4.5%, i.e. the very
 * screen-pixel thinking this wave exists to delete. Pure fractions scale
 * exactly, which is what `overlayRender.test.ts` pins.
 */
export const OVERLAY_GEOM = {
  /** Font size ÷ media width. */
  fontFraction: 0.045,
  /** Left/right margin ÷ media width (the editor's `mx-4` at phone widths). */
  marginFraction: 0.043,
  /** Vertical padding inside the bar, ÷ font size. */
  padFraction: 0.5,
  /**
   * Breathing room at each END of a line, ÷ font size — the editor's
   * `paddingLeft/Right`. The plate itself is unchanged by this; it is the
   * width the WORDS get, which is what decides where a line wraps.
   */
  padXFraction: 1,
  /** Distance between two wrapped lines, ÷ font size. */
  lineFraction: 1.25,
  /** Corner radius ÷ font size. */
  radiusFraction: 0.3,
  /** The bar's plate and its words. */
  plate: 'rgba(0, 0, 0, 0.5)',
  ink: '#FFFFFF',
  font: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
} as const;

/** The slice of the media actually on screen, as fractions of its size. */
export interface VisibleBand {
  top: number;
  bottom: number;
  /** Horizontal extent, 0-1 of the media's WIDTH. Omitted = all of it. */
  left?: number;
  right?: number;
}

const WHOLE: VisibleBand = { top: 0, bottom: 1, left: 0, right: 1 };

export interface RenderOverlayOptions {
  /**
   * Which slice of the picture the viewer can actually see, 0-1 of its height.
   *
   * A moment viewer FILLS the screen (`object-cover`, see `mediaFit.ts`), so a
   * portrait picture loses a little off the top and bottom — and a bar parked
   * hard against the edge would lose its words with it. Bars are nudged inside
   * this band so the writing always arrives whole. STROKES ARE NOT: a drawing
   * belongs to whatever it was drawn on, and sliding it would move his mark off
   * the thing he marked. Defaults to the whole picture, which is what the
   * editor, an `object-contain` viewer and the export bake all pass.
   */
  visible?: VisibleBand;
}

/**
 * Paint `overlays` onto a context sized `width` × `height`.
 *
 * Layer order — strokes first, bars on top — is the order they were baked in,
 * so old moments and new ones look the same.
 */
export function renderOverlays(
  ctx: CanvasRenderingContext2D,
  overlays: MomentOverlays | null | undefined,
  width: number,
  height: number,
  options: RenderOverlayOptions = {}
): void {
  if (!overlays || !(width > 0) || !(height > 0)) return;
  drawStrokes(ctx, overlays.strokes ?? [], width, height);
  drawBars(ctx, overlays.bars ?? [], width, height, options.visible ?? WHOLE);
}

/** Strokes only — normalized points scaled straight onto this canvas. */
export function drawStrokes(
  ctx: CanvasRenderingContext2D,
  strokes: OverlayStroke[],
  width: number,
  height: number
): void {
  for (const stroke of strokes) {
    const pts = stroke.pts;
    if (!pts || pts.length < 2) continue;

    ctx.beginPath();
    ctx.strokeStyle = stroke.color;
    // BUG-112: thickness is a fraction of the picture, so a line drawn on a
    // 390 px preview arrives as thick as it looked, not 2.8x thinner.
    ctx.lineWidth = Math.max(1, stroke.w * width);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.moveTo(pts[0][0] * width, pts[0][1] * height);
    for (let i = 1; i < pts.length; i++) {
      ctx.lineTo(pts[i][0] * width, pts[i][1] * height);
    }
    ctx.stroke();
  }
}

/**
 * Text bars only — inset rounded plates, centred text, clamped into `visible`.
 *
 * Every horizontal number is a fraction of the VISIBLE width, not the media's
 * (FEAT-040 gate finding): under `object-cover` the picture runs past both
 * screen edges, and a plate sized to the media ran with it — a square
 * edge-to-edge band with its rounded corners cropped off. In the visible
 * space the band is always the inset rounded bar the sender saw — for the
 * editor, a letterboxed viewer, and the export bake the visible width IS the
 * media width, so nothing changes there. STROKES stay in media space: a
 * drawing belongs to whatever it was drawn on.
 */
export function drawBars(
  ctx: CanvasRenderingContext2D,
  bars: OverlayBar[],
  width: number,
  height: number,
  visible: VisibleBand = WHOLE
): void {
  if (bars.length === 0) return;

  const visLeft = (visible.left ?? 0) * width;
  const visWidth = ((visible.right ?? 1) - (visible.left ?? 0)) * width;
  if (!(visWidth > 0)) return;

  const fontSize = visWidth * OVERLAY_GEOM.fontFraction;
  const margin = visWidth * OVERLAY_GEOM.marginFraction;
  const radius = fontSize * OVERLAY_GEOM.radiusFraction;
  const boxWidth = visWidth - margin * 2;
  const lineWidth = barLineWidth(boxWidth, fontSize);
  const step = fontSize * OVERLAY_GEOM.lineFraction;

  ctx.font = barFontSpec(fontSize);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Measured with the font already set above, so a line breaks where these
  // exact words at this exact size stop fitting — not where a guess says.
  const measure = textMeasurer(ctx, fontSize);

  for (const bar of bars) {
    // BUG-126: one `fillText` drew however many words he typed on ONE line,
    // and a long caption ran out both ends of its plate and off the picture.
    const lines = wrapBarText(bar.text, lineWidth, measure);
    const boxHeight = barPlateHeight(lines.length, fontSize);
    const centerY = barCenterY(bar.y, boxHeight, height, visible);
    const boxY = centerY - boxHeight / 2;

    ctx.fillStyle = OVERLAY_GEOM.plate;
    roundedRect(ctx, visLeft + margin, boxY, boxWidth, boxHeight, radius);
    ctx.fill();

    ctx.fillStyle = OVERLAY_GEOM.ink;
    // The block is centred on the plate's middle, which is what the editor
    // does too — so a bar keeps its place as it grows a second line.
    const firstY = centerY - ((lines.length - 1) * step) / 2;
    lines.forEach((line, i) => {
      ctx.fillText(line, visLeft + visWidth / 2, firstY + i * step);
    });
  }
}

/** The font string every surface writes a bar with — one spelling, one look. */
export function barFontSpec(fontSize: number): string {
  return `500 ${fontSize}px ${OVERLAY_GEOM.font}`;
}

/** How wide the WORDS may run inside a plate `boxWidth` across. */
export function barLineWidth(boxWidth: number, fontSize: number): number {
  return boxWidth - fontSize * OVERLAY_GEOM.padXFraction * 2;
}

/**
 * How tall a plate holding `lines` lines is.
 *
 * One line is exactly what it always was, to the pixel — the overwhelming
 * majority of captions are one line, and they must not move.
 */
export function barPlateHeight(lines: number, fontSize: number): number {
  const n = Math.max(1, lines);
  const text = fontSize * (1 + (n - 1) * OVERLAY_GEOM.lineFraction);
  return text + fontSize * OVERLAY_GEOM.padFraction * 2;
}

/** Anything that can say how wide a run of text is at the current font. */
export type MeasureText = (text: string) => number;

/** Rough average glyph width ÷ font size, for contexts that cannot measure. */
const ESTIMATED_CHAR_WIDTH = 0.55;

/**
 * A measurer for `ctx`, falling back to an estimate where `measureText` isn't.
 *
 * Not paranoia: jsdom's 2d context is a stub without it, and so is any context
 * a future surface hands us. A caption that cannot be measured still has to
 * wrap SOMEWHERE — roughly right beats running off the picture.
 */
function textMeasurer(
  ctx: CanvasRenderingContext2D | null,
  fontSize: number
): MeasureText {
  if (ctx && typeof ctx.measureText === 'function') {
    return (text) => ctx.measureText(text).width;
  }
  return (text) => text.length * fontSize * ESTIMATED_CHAR_WIDTH;
}

/**
 * Break `text` into the lines a plate `maxWidth` wide can hold.
 *
 * Greedy, word by word, the way a paragraph wraps — and a single word longer
 * than the whole plate (a link, a laugh with forty h's) is cut mid-word,
 * because the alternative is the very overflow this exists to stop.
 */
export function wrapBarText(
  text: string,
  maxWidth: number,
  measure: MeasureText
): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [text];
  if (!(maxWidth > 0)) return [words.join(' ')];

  const lines: string[] = [];
  let line = '';

  for (const word of words) {
    if (!line) {
      line = word;
    } else if (measure(`${line} ${word}`) <= maxWidth) {
      line = `${line} ${word}`;
      continue; // it fits by construction — nothing left to break
    } else {
      lines.push(line);
      line = word;
    }

    // Only a lone word can still be too wide at this point. Cut it by
    // GRAPHEME, never by code unit: `'😂😂'.slice(0, 3)` is half a surrogate
    // pair, which paints as tofu — a caption of emoji with no spaces is an
    // ordinary thing to send.
    while (measure(line) > maxWidth) {
      const units = graphemes(line);
      if (units.length < 2) break;
      let cut = units.length - 1;
      const upTo = (n: number) => units.slice(0, n).join('');
      while (cut > 1 && measure(upTo(cut)) > maxWidth) cut--;
      lines.push(upTo(cut));
      line = units.slice(cut).join('');
    }
  }

  if (line) lines.push(line);
  return lines;
}

/** One user-perceived character — an emoji and its modifiers stay together. */
interface GraphemeSegmenter {
  segment(input: string): Iterable<{ segment: string }>;
}
type SegmenterCtor = new (
  locales?: string | string[],
  options?: { granularity: 'grapheme' }
) => GraphemeSegmenter;

let segmenter: GraphemeSegmenter | null | undefined;

/** `text` split into grapheme clusters, or code POINTS where Intl can't. */
function graphemes(text: string): string[] {
  if (segmenter === undefined) {
    const ctor = (Intl as unknown as { Segmenter?: SegmenterCtor }).Segmenter;
    segmenter = ctor ? new ctor(undefined, { granularity: 'grapheme' }) : null;
  }
  // Even the fallback iterates code points, so a surrogate pair is never split.
  if (!segmenter) return Array.from(text);
  return Array.from(segmenter.segment(text), (piece) => piece.segment);
}

/**
 * The same lines, for a surface that has no drawing context — the editor,
 * which lays its bar out in the DOM but must break it exactly where the
 * recipient's canvas will. Measured on a scratch canvas with the bar's own
 * font, so what he places is what she opens.
 */
let scratch: CanvasRenderingContext2D | null | undefined;

export function barTextLines(
  text: string,
  fontSize: number,
  maxWidth: number
): string[] {
  if (scratch === undefined) {
    scratch =
      typeof document === 'undefined'
        ? null
        : document.createElement('canvas').getContext('2d');
  }
  if (scratch) scratch.font = barFontSpec(fontSize);
  return wrapBarText(text, maxWidth, textMeasurer(scratch, fontSize));
}

/**
 * Where a bar's middle lands, in this canvas's pixels.
 *
 * Its placement is `y` % down the picture; the only adjustment is the nudge
 * that keeps the whole plate inside the visible band. When the band is the
 * whole picture (editor, export, letterboxed viewer) this is a plain multiply.
 * A band too short to hold the bar centres it rather than picking an edge —
 * with nowhere safe to put it, the middle is the least wrong answer.
 */
export function barCenterY(
  yPercent: number,
  boxHeight: number,
  height: number,
  visible: VisibleBand = WHOLE
): number {
  const raw = (yPercent / 100) * height;
  const top = visible.top * height + boxHeight / 2;
  const bottom = visible.bottom * height - boxHeight / 2;
  if (!(bottom > top)) return (visible.top + visible.bottom) * 0.5 * height;
  return Math.min(Math.max(raw, top), bottom);
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/** Nothing to draw? Then there is nothing to send, store, or render. */
export function hasOverlays(overlays: MomentOverlays | null | undefined): boolean {
  if (!overlays) return false;
  return (overlays.bars?.length ?? 0) > 0 || (overlays.strokes?.length ?? 0) > 0;
}
